import D from "@/admin/dashboard/Dashboard.module.css";

import { adminStoragePoolQueryAtom } from "@/admin/buckets/storagePoolsQuery.ts";
import { BigDonut } from "@/admin/dashboard/DonutChart.tsx";
import { LegendTable } from "@/admin/dashboard/LegendTable.tsx";
import { LightBlock } from "@/admin/dashboard/LightBlock.tsx";
import { Separator } from "@/components/Separator.tsx";
import { locationSlugsAtom } from "@/modules/api/locations.store.ts";
import { SuspenseErrorBoundary } from "@/modules/error/ErrorFallback.tsx";
import { useSuspenseQueryAtom } from "@/utils/query/useSuspenseQueryAtom.ts";
import { useAtomValue } from "jotai";

function storageGB(x: string) {
    const units = ["GB", "TB", "PB", "EB", "ZB", "YB"];
    let l = 0;
    let n = parseInt(x, 10) || 0;

    while (n >= 1024 && ++l) {
        n = n / 1024;
    }
    //include a decimal point and a tenths-place digit if presenting
    //less than ten of KB or greater units
    return `${n.toFixed(n < 10 && l > 0 ? 1 : 0)} ${units[l]}`;
}

function LocationStorage({ location }: { location: string }) {
    const storagePools = useSuspenseQueryAtom(adminStoragePoolQueryAtom(location));

    return [...storagePools.values()]
        .sort((a, b) => a.display_name.localeCompare(b.display_name))
        .map((item) => {
            const { uuid, display_name, percent_used, stored_gb, total_gb } = item;

            const items = [
                { value: stored_gb, color: "var(--color-chart-2)" },
                { value: total_gb - stored_gb, color: "var(--color-chart-free)" },
            ];

            const tooltipContent = (
                <LegendTable
                    items={[
                        { label: "Stored:", value: storageGB(stored_gb.toFixed(0)), color: "color-chart-2" },
                        {
                            label: "Available:",
                            value: storageGB((total_gb - stored_gb).toFixed(0)),
                            color: "color-chart-free",
                        },
                    ]}
                />
            );

            return (
                <BigDonut
                    key={uuid}
                    displayName={display_name}
                    items={items}
                    tooltipContent={tooltipContent}
                    total={storageGB(total_gb.toFixed(0))}
                    location={location}
                    percentUsed={percent_used}
                />
            );
        });
}

function StorageSectionContent() {
    const locations = useAtomValue(locationSlugsAtom);

    return (
        <>
            <h2 className="font-size-heading">Storage</h2>
            <p className="pb-3">Block storage usage</p>
            <Separator />
            <div className={D.Grid}>
                {locations.map((location) => (
                    // Separate error boundary for each location so one failure doesn't break the whole section
                    <SuspenseErrorBoundary key={location} loadingHeight="260px" tags={["admin", "StorageSection"]}>
                        <LocationStorage location={location} />
                    </SuspenseErrorBoundary>
                ))}
            </div>
        </>
    );
}

export function StorageSection() {
    return (
        <LightBlock className={D.Wide}>
            <StorageSectionContent />
        </LightBlock>
    );
}
